<template>
  <div v-if="user" class="navbar-container d-flex content align-items-center">
    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link class="nav-link text-primary" @click="toggleVerticalMenuActive">
          <feather-icon icon="MenuIcon" size="21" />
          Meny
        </b-link>
      </li>
    </ul>

    <!-- Left Col -->
    <div
      class="bookmark-wrapper align-items-center flex-grow-1 d-none d-lg-flex"
    >
      <dark-Toggler class="d-none d-lg-block" />
    </div>

    <b-navbar-nav class="nav align-items-center ml-auto">
      <b-nav-item-dropdown
        right
        toggle-class="d-flex align-items-center dropdown-user-link"
        class="dropdown-user"
      >
        <template #button-content>
          <div class="d-sm-flex d-none user-nav">
            <p class="user-name font-weight-bolder mb-0">
              {{ user.first_name + " " + user.last_name }}
            </p>
            <span
              v-if="portfolioOwner && user && user.id !== portfolioOwner.id"
              class="user-status"
              >Portfölj:
              {{
                portfolioOwner.first_name + " " + portfolioOwner.last_name
              }}</span
            >
          </div>
          <b-avatar
            v-if="user && user.avatar"
            badge-variant="primary"
            variant="light-primary"
            :src="imageUrl"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="user.first_name"
            badge-variant="primary"
            variant="light-primary"
            :text="user.first_name[0] + user.last_name[0]"
            class="badge-minimal"
          />
        </template>

        <b-dropdown-header v-if="portfolios.length > 0">
          Välj portfölj
        </b-dropdown-header>
        <b-dropdown-divider />
        <b-dropdown-item
          v-for="portfolio in portfolios"
          :key="portfolio.id"
          link-class="d-flex align-items-center"
          @click="
            setNewActivePortfolio(
              portfolio.id,
              portfolio.blocked || isPortfolioBlocked(portfolio.id)
            )
          "
        >
          <b-avatar
            v-if="
              currentOwner &&
              portfolio.owner.id == currentOwner.user_id &&
              (portfolio.blocked || isPortfolioBlocked(portfolio.id)) &&
              currentOwner.avatar
            "
            badge="✓"
            badge-variant="primary"
            variant="light-danger"
            :src="currentOwnerAvatar"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="
              currentOwner &&
              portfolio.owner.id == currentOwner.user_id &&
              (portfolio.blocked || isPortfolioBlocked(portfolio.id)) &&
              !currentOwner.avatar
            "
            badge="✓"
            badge-variant="primary"
            variant="light-danger"
            :text="portfolio.owner.first_name[0] + portfolio.owner.last_name[0]"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="
              (portfolio.blocked || isPortfolioBlocked(portfolio.id)) &&
              portfolio.owner.avatar
            "
            variant="light-danger"
            :src="portfolioAvatar"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="
              (portfolio.blocked || isPortfolioBlocked(portfolio.id)) &&
              !portfolio.owner.avatar
            "
            variant="light-danger"
            :text="portfolio.owner.first_name[0] + portfolio.owner.last_name[0]"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="
              currentOwner &&
              portfolio.owner.id == currentOwner.user_id &&
              currentOwner.avatar
            "
            badge="✓"
            badge-variant="primary"
            variant="light-primary"
            :src="currentOwnerAvatar"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="
              currentOwner &&
              portfolio.owner.id == currentOwner.user_id &&
              !currentOwner.avatar
            "
            badge="✓"
            badge-variant="primary"
            variant="light-primary"
            :text="portfolio.owner.first_name[0] + portfolio.owner.last_name[0]"
            class="badge-minimal"
          />
          <b-avatar
            v-else-if="portfolio.owner.avatar"
            size="40"
            variant="light-primary"
            :src="portfolioAvatar"
          />
          <b-avatar
            v-else
            size="40"
            variant="light-primary"
            :text="portfolio.owner.first_name[0] + portfolio.owner.last_name[0]"
          />
          <span class="ml-1"
            >{{ portfolio.owner.first_name }} {{ portfolio.owner.last_name }} -
            {{ portfolio.id }}</span
          >
        </b-dropdown-item>

        <!-- länka till konto för inloggad user -->
        <b-dropdown-header> Mitt konto </b-dropdown-header>
        <b-dropdown-divider />
        <b-dropdown-item
          @click="$router.push({ name: 'account', params: { id: user.id } })"
          link-class="d-flex align-items-center"
        >
          <feather-icon size="16" icon="UserIcon" class="mr-50" />
          Mitt konto
          <b-dropdown-divider />
        </b-dropdown-item>
        <b-dropdown-item @click="logout" link-class="d-flex align-items-center">
          <feather-icon size="16" icon="LogOutIcon" class="mr-50" />
          <span>Logga ut</span>
        </b-dropdown-item>
      </b-nav-item-dropdown>
    </b-navbar-nav>
  </div>
</template>

<script>
import {
  BLink,
  BNavbarNav,
  BNavItemDropdown,
  BDropdownItem,
  BDropdownHeader,
  BDropdownDivider,
  BAvatar,
} from "bootstrap-vue";
import DarkToggler from "@core/layouts/components/app-navbar/components/DarkToggler.vue";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

export default {
  data() {
    return {
      currentContact: null,
      currentOwner: null,
      imageUrl: null,
      currentOwnerAvatar: null,
      portfolioAvatar: null,
    };
  },

  async mounted() {
    if (this.user && this.user.avatar) {
      await this.fetchImageWithToken(this.user.avatar.small, "user");
    }
    if (this.owner && this.owner.avatar) {
      await this.fetchImageWithToken(this.owner.avatar.small, "owner");
    }
    await this.fetchImagesForPortfolios();
  },
  computed: {
    user() {
      let user = this.getUser();
      return user ? user : {};
    },
    portfolioOwner: function () {
      return this.getPortfolioOwner();
    },
    activePortfolio: function () {
      return this.getActivePortfolio();
    },
    portfolios() {
      let portfolios = this.getAllPortfolios().filter((p) => p.owner.id);
      return portfolios;
    },
    owner: function () {
      return this.getOwner();
    },
  },
  methods: {
    ...mapGetters([
      "getContactByUserId",
      "getOwner",
      "getAllPortfolios",
      "getActivePortfolio",
    ]),
    ...mapGetters("userAccount", [
      "getBlockedPortfolios",
      "getPortfolioOwner",
      "getUser",
    ]),
    ...mapActions(["setActivePortfolio"]),
    fetchImagesForPortfolios() {
      const portfolios = this.portfolios;
      portfolios.forEach((p) => {
        if (p.owner.Avatar && p.owner.Avatar.small) {
          this.fetchImageWithToken(p.owner.avatar.small, "portfolio");
        }
      });
    },

    async fetchImageWithToken(imageUrl, type) {
      try {
        const token = JSON.parse(localStorage.getItem("user")).jwt;

        const response = await axios.get(imageUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        });

        if (type === "user") this.imageUrl = URL.createObjectURL(response.data);
        if (type === "owner")
          this.currentOwnerAvatar = URL.createObjectURL(response.data);
        if (type === "portfolio")
          this.portfolioAvatar = URL.createObjectURL(response.data);
      } catch (error) {
        return "";
      }
    },
    logout() {
      this.$store.dispatch("logout");
      this.$router.push({ name: "login" });
    },
    fetchContactByUserId: function (id) {
      return this.getContactByUserId(id);
    },
    setNewActivePortfolio(portfolioId, blocked) {
      if (blocked) return;
      this.setActivePortfolio(portfolioId);
      this.$router.push({ name: "news-feed" });
    },
    isPortfolioBlocked: function (portfolioId) {
      const blockedPortfolios = this.getBlockedPortfolios();
      return blockedPortfolios.find((p) => p.id == portfolioId);
    },
  },
  watch: {
    contact: function (newVal) {
      this.currentContact = newVal;
    },
    owner: function (newVal) {
      this.currentOwner = newVal;
    },
  },
  components: {
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownHeader,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,

    // Navbar Components
    DarkToggler,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
};
</script>
<style>
.header-navbar .navbar-container ul.navbar-nav li.dropdown-user .dropdown-menu {
  width: auto;
}
</style>
